import React, { FC, useMemo, useState } from 'react';
import { Col, Form, Input, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { i18nKey } from 'src/locales/i18n';
import styles from './company-detail.module.less';
import TitleBox from 'src/components/title-box/title-box';
import { CURRENCY_OPTIONS, ONBOARDING_CURRENCY_TYPE } from 'src/constants';
import useStore from 'src/hooks/useStore';
import { ICorporateOnboardingStore } from 'src/store/corporate/onboarding.store';
import { observer } from 'mobx-react-lite';

import CountryDistrictSection from 'src/components/country-district-section/country-district-section';
import CurrencyInput from 'src/components/input-with-currency/input-with-currency';
import { ZIPCODE_REGEX } from 'src/constants/validation';

const CompanyDetailForm: FC = () => {
	const corporateOnboardingStore: ICorporateOnboardingStore = useStore('corporateOnboardingStore');
	const [disableZipCode, setDisableZipCode] = useState(false);
	const [currency, setCurrency] = useState(
		corporateOnboardingStore.contractPerson && corporateOnboardingStore.contractPerson.referenceCurrency
			? corporateOnboardingStore.contractPerson.referenceCurrency
			: ONBOARDING_CURRENCY_TYPE.HKD
	);

	const [t] = useTranslation();

	const [accountForm] = Form.useForm();

	const years = useMemo(() => {
		const currentYear = new Date().getFullYear();
		const rs = [];
		for (let i = currentYear; i >= currentYear - 2; i--) {
			rs.push({
				key: '' + i,
				value: i,
				label: i
			});
		}
		return rs;
	}, []);

	React.useEffect(() => {
		if (!accountForm.isFieldsTouched()) {
			accountForm.setFieldsValue({
				...corporateOnboardingStore.companyDetail,
				referenceCurrency:
					corporateOnboardingStore.contractPerson && corporateOnboardingStore.contractPerson.referenceCurrency
						? corporateOnboardingStore.contractPerson.referenceCurrency
						: ONBOARDING_CURRENCY_TYPE.HKD
			});

			if (corporateOnboardingStore.companyDetail && corporateOnboardingStore.companyDetail?.country === 'HK') {
				setDisableZipCode(true);
			}
		}
	}, [corporateOnboardingStore.companyDetail]);
	return (
		<Form
			form={accountForm}
			layout="vertical"
			disabled
			initialValues={{
				...corporateOnboardingStore.companyDetail,
				referenceCurrency:
					corporateOnboardingStore.companyDetail && corporateOnboardingStore.companyDetail.referenceCurrency
						? corporateOnboardingStore.companyDetail.referenceCurrency
						: ONBOARDING_CURRENCY_TYPE.HKD
			}}>
			<Row gutter={[32, 16]} className="common-padding">
				<Col xs={24} sm={24} md={24} lg={12}>
					<Form.Item label={`${t(i18nKey.onboarding.request.employment.currency)}`}>
						<Row>
							<Col xs={24} sm={24} md={24} lg={6} style={{ width: '100%' }}>
								<Form.Item noStyle name="referenceCurrency">
									<Select
										placeholder={`${t(i18nKey.common.select)}`}
										options={CURRENCY_OPTIONS}
										style={{ width: '100%' }}
										onChange={(e) => {
											setCurrency(e);
										}}
									/>
								</Form.Item>
							</Col>
						</Row>
					</Form.Item>
				</Col>
			</Row>
			<TitleBox>
				<span>{t(i18nKey.onboarding.corporate.companyDetail.title)}</span>
			</TitleBox>

			<div className={styles.subLevelForm}>
				<Row gutter={[32, 16]}>
					<Col span={24}>
						<Form.Item name="name" label={t(i18nKey.registration.companyName)} required={false}>
							<Input disabled />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={[32, 16]}>
					<Col span={24}>
						<Form.Item
							name="registeredAddress"
							label={t(i18nKey.onboarding.corporate.companyDetail.companyAddress)}
							required={false}>
							<Input disabled />
						</Form.Item>
					</Col>
				</Row>
			</div>
			<div className={styles.subLevelForm}>
				<Row gutter={[32, 16]}>
					<Col span={24}>
						<Row>
							<Col span={24} className={styles.titleName}>
								{t(i18nKey.dashboard.accountInformation.mailingAddressReg)}
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								<div className={styles.dividerLine}></div>
							</Col>
						</Row>

						<Row gutter={[32, 16]}>
							<Col span={24}>
								<Form.Item name="addressLine" label={`${t(i18nKey.dashboard.accountInformation.adrLine)}`}>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={[32, 16]}>
							<Col span={24}>
								<Form.Item name="street" label={`${t(i18nKey.dashboard.accountInformation.street)}`}>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<CountryDistrictSection
							countryName={'country'}
							countryLabel={`${t(i18nKey.dashboard.accountInformation.country)}`}
							districtName={'district'}
							districtLabel={`${t(i18nKey.dashboard.accountInformation.district)}`}
						/>
						<Row gutter={[32, 16]}>
							{!disableZipCode && (
								<Col span={24}>
									<Form.Item
										name="zipCode"
										label={`${t(i18nKey.dashboard.accountInformation.zipCode)}`}
										rules={[
											{
												pattern: ZIPCODE_REGEX,
												message: `${t(i18nKey.onboarding.validation.zipCode)}`
											}
										]}>
										<Input />
									</Form.Item>
								</Col>
							)}
						</Row>
						<Row gutter={[32, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									name="website"
									label={`${t(i18nKey.onboarding.corporate.companyDetail.website)}*`}
									required={false}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										},
										{
											type: 'url',
											message: `${t(i18nKey.onboarding.validation.companyDetail.websiteFormatRequired)}`
										}
									]}>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={[32, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<div className="ant-form-item-label">
									<label>{t(i18nKey.onboarding.corporate.companyDetail.netProfit)}*</label>
								</div>
							</Col>
						</Row>
						<Row className={styles.revenueRow} gutter={[16, 2]}>
							<Col xs={2} sm={2} md={2} lg={1}>
								<div className="ant-form-item-label">
									<label>{'1)'}</label>
								</div>
							</Col>
							<Col xs={10} sm={10} md={10} lg={11}>
								<Form.Item>
									<CurrencyInput
										name={['netProfits', 0, 'revenue']}
										currency={currency}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}
									/>
								</Form.Item>
							</Col>
							<Col xs={4} sm={4} md={4} lg={4}>
								<div className="ant-form-item-label">
									<label>{t(i18nKey.dashboard.accountInformation.year)}</label>
								</div>
							</Col>
							<Col xs={8} sm={8} md={8} lg={5}>
								<Form.Item name={['netProfits', 0, 'year']}>
									<Select placeholder={`${t(i18nKey.common.select)}`} options={years} />
								</Form.Item>
							</Col>
						</Row>
						<Row className={styles.revenueRow} gutter={[16, 2]}>
							<Col xs={2} sm={2} md={2} lg={1}>
								<div className="ant-form-item-label">
									<label>{'2)'}</label>
								</div>
							</Col>
							<Col xs={10} sm={10} md={10} lg={11}>
								<Form.Item>
									<CurrencyInput
										name={['netProfits', 1, 'revenue']}
										currency={currency}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}
									/>
								</Form.Item>
							</Col>
							<Col xs={4} sm={4} md={4} lg={4}>
								<div className="ant-form-item-label">
									<label>{t(i18nKey.dashboard.accountInformation.year)}</label>
								</div>
							</Col>
							<Col xs={8} sm={8} md={8} lg={5}>
								<Form.Item name={['netProfits', 1, 'year']}>
									<Select placeholder={`${t(i18nKey.common.select)}`} options={years} />
								</Form.Item>
							</Col>
						</Row>
						<Row className={styles.revenueRow} gutter={[16, 2]}>
							<Col xs={2} sm={2} md={2} lg={1}>
								<div className="ant-form-item-label">
									<label>{'2)'}</label>
								</div>
							</Col>
							<Col xs={10} sm={10} md={10} lg={11}>
								<Form.Item>
									<CurrencyInput
										name={['netProfits', 2, 'revenue']}
										currency={currency}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}
									/>
								</Form.Item>
							</Col>
							<Col xs={4} sm={4} md={4} lg={4}>
								<div className="ant-form-item-label">
									<label>{t(i18nKey.dashboard.accountInformation.year)}</label>
								</div>
							</Col>
							<Col xs={8} sm={8} md={8} lg={5}>
								<Form.Item name={['netProfits', 2, 'year']}>
									<Select placeholder={`${t(i18nKey.common.select)}`} options={years} />
								</Form.Item>
							</Col>
						</Row>
					</Col>
				</Row>
			</div>
		</Form>
	);
};

export default observer(CompanyDetailForm);
